import React from 'react';
import Alert$1 from '@amzn/awsui-components-react/polaris/alert';
import { renderReact } from '../utils/markdown.plugin.utils.js';
import sanitizeHtml from '../utils/sanitize.utils.js';

const renderAlert = (tokens, idx, options, env, self) => {
  const html = self.render(tokens[idx].children, options, env);
  if (html && !sanitizeHtml(html)) return; // check sanitize html

  return renderReact(_ref => {
    let {
      content
    } = _ref;
    const {
      headerText,
      type,
      isEmpty
    } = content.meta;
    return /*#__PURE__*/React.createElement(Alert$1, {
      header: headerText,
      type: type,
      className: "bork-alert"
    }, !isEmpty && /*#__PURE__*/React.createElement("div", {
      className: "bork-alert-content",
      dangerouslySetInnerHTML: {
        __html: self.render(tokens[idx].children, options, env)
      }
    }));
  })({
    content: tokens[idx],
    origRender: '',
    env,
    tokens
  });
};
const isWhitespace = (state, start, end) => {
  for (start; start < end; start++) {
    if (!state.md.utils.isWhiteSpace(state.src.charCodeAt(start))) return false;
  }
  return true;
};
const isOpenBlock = (state, start, max, OPEN_MARKER_STR, OPEN_MARKER_LEN) => {
  // get current line
  const currentLine = state.src.slice(start, max);

  // line begins with :::alert
  if (currentLine.slice(0, OPEN_MARKER_LEN) === OPEN_MARKER_STR) {
    // remove any {{params}} from line
    const cleanedLine = currentLine.replace(/\{{[^]*\}}/, '');
    const charCode = cleanedLine.charCodeAt(OPEN_MARKER_LEN);

    // covers valid case for description only alerts
    if (isNaN(charCode)) return true;

    // if next character is white space then valid :::alert Title Text
    return state.md.utils.isWhiteSpace(charCode);
  }
  return false;
};
const BASE_MARKER = ':::';
const OPEN_MARKER_STR = "".concat(BASE_MARKER, "alert");
const OPEN_MARKER_LEN = OPEN_MARKER_STR.length;
const OPEN_MARKER_CHAR = OPEN_MARKER_STR.charCodeAt(0); // :
const CLOSE_MARKER_STR = BASE_MARKER;

// The core plugin which checks for the appropriate prefix content of
// `:::alert` for an alert container block and passed params `{{ params }}`
const coreRule = (state, startLine, endLine, silent) => {
  let pos, isFound;
  let openBlockCount = 0;
  let start = state.bMarks[startLine] + state.tShift[startLine];
  let max = state.eMarks[startLine];

  // Check out the first character quickly,
  // this should filter out most of non-alerts
  if (state.src.charCodeAt(start) !== OPEN_MARKER_CHAR) return false;
  const validOpenBlock = isOpenBlock(state, start, max, OPEN_MARKER_STR, OPEN_MARKER_LEN);
  if (!validOpenBlock) return false;

  // Check out the rest of the marker string and set position (pos)
  for (pos = start + 1; pos <= max; pos++) {
    if (OPEN_MARKER_STR[(pos - start) % OPEN_MARKER_LEN] !== state.src[pos]) {
      break;
    }
  }
  pos -= (pos - start) % OPEN_MARKER_LEN;
  const params = state.src.slice(pos, max).trim();

  // Since start is found, we can report success here in validation mode
  if (silent) return true;

  // Search the end of the block
  let nextLine = startLine;
  let isEmpty = true; // keep track of alert content

  for (;;) {
    nextLine++;

    // If current line begins with :::alert, increment openBlockCount
    // Or if current line begins with :::someOtherBlockComponent, increment openBlockCount
    if (state.src.slice(start, pos) === OPEN_MARKER_STR || state.src.slice(start, start + 3) === BASE_MARKER && state.md.utils.isWhiteSpace(state.src.charCodeAt(start + 3)) === false) {
      openBlockCount++;
    }

    // Unclosed block should be autoclosed by end of document.
    if (nextLine >= endLine) break;
    start = state.bMarks[nextLine] + state.tShift[nextLine];
    max = state.eMarks[nextLine];
    pos = start + OPEN_MARKER_LEN;

    // Non-empty line with negative indent should stop the list
    if (start < max && state.sCount[nextLine] < state.blkIndent) break;

    // Encountered a closing marker line :::
    if (state.src.slice(start, max) === CLOSE_MARKER_STR && openBlockCount === 1) {
      isFound = true;
      break;
    }
    if (state.src.slice(start, max) !== CLOSE_MARKER_STR) {
      if (isEmpty) isEmpty = isWhitespace(state, start, max);
      continue;
    }

    // If more than open block has been counted, decrement and continue parsing through lines
    if (openBlockCount > 1) {
      openBlockCount--;
      continue;
    }
  }
  if (!isFound) return false;
  const oldParent = state.parentType;
  const oldLineMax = state.lineMax;
  state.parentType = 'reference';

  // This will prevent lazy continuations from ever going past our end marker
  state.lineMax = nextLine;

  // define default parameter values
  let headerText = params;
  let type = 'info';

  // check for alert parameters
  const PARAMS_EXP = new RegExp('{{(.*?)}}', 'i');
  const optionalParams = PARAMS_EXP.exec(params);
  if (optionalParams) {
    // checks first line for parameters and splits into passedParams and headerText
    const baseFirstLineString = optionalParams[0];
    const passedParams = optionalParams[1].split(',').map(elem => elem.trim());
    headerText = headerText.replace(baseFirstLineString, '').trim();

    // parse through params and update values
    passedParams.forEach(param => {
      if (param.includes('type')) {
        const typeStr = param.split('=');
        type = typeStr[1];
      }
    });
  }

  // no render if both alert header text and contents are empty
  if (!headerText && isEmpty) return false;
  let token = state.push('alert', 'div', 0);
  token.block = true;
  token.meta = {
    headerText,
    type,
    isEmpty
  };

  // Grab markdown between open and closing markers :::
  const startingPoint = state.eMarks[startLine];
  const endingPoint = state.eMarks[nextLine - 1] + state.sCount[nextLine];
  const markup = state.src.slice(startingPoint, endingPoint).trim();
  token.map = [startLine, nextLine];
  token.content = params;
  token.children = state.md.parse(markup, state.env);
  state.parentType = oldParent;
  state.lineMax = oldLineMax;
  state.line = nextLine + 1;
  return true;
};
const alertPlugin = md => {
  md.block.ruler.before('fence', 'alert', coreRule, {
    alt: ['paragraph', 'reference', 'blockquote', 'list']
  });
  md.renderer.rules.alert = renderAlert;
};
var Alert = (() => ({
  plugin: alertPlugin
}));

export { Alert as default };
